import React, { Component, type ComponentType, type ReactNode } from 'react';
import { styled } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { ProjectSettingsErrorScreen } from '@atlassian/jira-common-components-project-settings-error-screen';
import { UnloadPrompt } from '@atlassian/jira-common-components-unload-prompt';
import {
	type IssueContainerName,
	PROJECT_ISSUE_CONTAINER_NAME,
} from '@atlassian/jira-common-constants/src/issue-container-names';
import type { ProjectType } from '@atlassian/jira-common-constants/src/project-types';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { componentWithFF } from '@atlassian/jira-feature-flagging-utils';
import { fg } from '@atlassian/jira-feature-gating';
import {
	ContextualAnalyticsData,
	SCREEN,
	FireScreenAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import SetCustomMark from '@atlassian/jira-providers-spa-apdex-analytics/src/set-custom-mark/index.tsx';
import SubmitApdex from '@atlassian/jira-providers-spa-apdex-analytics/src/submit-apdex-mark/index.tsx';
import { ProjectSettingsDetailsSkeleton } from '@atlassian/jira-skeletons/src/ui/project-settings/index.tsx';
import { SpaStatePageReady } from '@atlassian/jira-spa-state-controller/src/components/main.tsx';
import {
	PROJECT_DETAILS_APP_NAME,
	READY_MARK_KEY,
	TIME_TO_LOAD_MARK_KEY,
} from '../model/constants';
import type { ProjectId } from '../model/types';
import type { Flag } from '../state/ui/types';
import { projectSettingsDetailsPagePageLoad } from '../utils/performance-analytics';

type ProjectDetailsProps = {
	isAvatarPickerDialogOpen: boolean;
	isDeleteProjectModalOpen: boolean;
	isTrashProjectModalOpen: boolean;
	isArchiveProjectModalOpen: boolean;
	isFetchingData: boolean;
	isFormDirty: boolean;
	hasFetchDataError: boolean;
	flags: Flag[];
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	FormComponent: ComponentType<Record<any, any>>;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	AvatarPickerDialogComponent: ComponentType<Record<any, any>>;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	FlagsComponent: ComponentType<Record<any, any>>;
	DeleteProjectModalComponent: ComponentType<{
		issueContainerName: IssueContainerName;
	}>;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	SoftDeleteProjectModalComponent: ComponentType<Record<any, any>>;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	ArchiveProjectModalComponent: ComponentType<Record<any, any>>;
	HeaderComponent: ComponentType<{
		issueContainerName: IssueContainerName;
	}>;
	onRefresh: () => void;
};
export type Props = ProjectDetailsProps & {
	projectType: ProjectType;
	projectId: ProjectId;
};

// eslint-disable-next-line jira/react/no-class-components
export class ProjectDetails extends Component<ProjectDetailsProps> {
	renderContent() {
		const { hasFetchDataError, onRefresh, isFetchingData, isFormDirty, FormComponent } = this.props;

		if (hasFetchDataError) {
			return <ProjectSettingsErrorScreen onRefresh={onRefresh} />;
		}

		// Why Blanket and not skip rendering? Because there is logic deep down inside the FormComponent
		// that imports another app and that fires actions that are necessary for the loading process
		// so that if we _not_ render FormComponent then project-details never loads.
		return (
			<>
				{isFetchingData && <ProjectSettingsDetailsSkeleton isListOnly />}

				<Blanket hideContent={isFetchingData}>
					{!fg('hix-4189-releasing-historical-project-keys') && (
						<UnloadPrompt beforeUnload={isFormDirty} />
					)}
					<FormComponent />
					{!isFetchingData ? <SetCustomMark name={READY_MARK_KEY} /> : null}
					{!isFetchingData ? (
						<SubmitApdex
							appName={PROJECT_DETAILS_APP_NAME}
							// @ts-expect-error - TS2769 - No overload matches this call.
							metric={projectSettingsDetailsPagePageLoad}
						/>
					) : null}
				</Blanket>
				{!isFetchingData && <SpaStatePageReady />}
			</>
		);
	}

	render() {
		const {
			flags,
			isAvatarPickerDialogOpen,
			isDeleteProjectModalOpen,
			isTrashProjectModalOpen,
			isArchiveProjectModalOpen,
			AvatarPickerDialogComponent,
			FlagsComponent,
			HeaderComponent,
			SoftDeleteProjectModalComponent,
			DeleteProjectModalComponent,
			ArchiveProjectModalComponent,
		} = this.props;

		return (
			<Container>
				<SetCustomMark name={TIME_TO_LOAD_MARK_KEY} />
				<HeaderComponent issueContainerName={PROJECT_ISSUE_CONTAINER_NAME} />
				<FormWrapper>{this.renderContent()}</FormWrapper>
				{flags.length ? <FlagsComponent /> : null}
				{isAvatarPickerDialogOpen ? <AvatarPickerDialogComponent /> : null}
				{isDeleteProjectModalOpen ? (
					<DeleteProjectModalComponent issueContainerName={PROJECT_ISSUE_CONTAINER_NAME} />
				) : null}
				{isTrashProjectModalOpen ? <SoftDeleteProjectModalComponent /> : null}
				{isArchiveProjectModalOpen ? <ArchiveProjectModalComponent /> : null}
			</Container>
		);
	}
}

// eslint-disable-next-line jira/import/no-anonymous-default-export
export default ({ projectType, projectId, ...restProps }: Props) => (
	<ContextualAnalyticsData
		sourceName="projectDetails"
		sourceType={SCREEN}
		attributes={{
			projectType,
			projectId,
		}}
		containerType="project"
		containerId={String(projectId)}
	>
		<ProjectDetails {...restProps} />
		<FireScreenAnalytics />
	</ContextualAnalyticsData>
);

// the min-height is to prevent the content at the bottom of
// the avatar-picker-dialog from being cut off. See DONUT-1693

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContainerOld = styled.div<{
	children?: ReactNode;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	minHeight: `${gridSize * 70}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContainerNew = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	minHeight: `${gridSize * 70}px`,
	paddingBottom: token('space.1000'),
});

const Container = componentWithFF(
	'hix-4818-adopt-modern-project-details-page-across-all-products_3zvy1',
	ContainerNew,
	ContainerOld,
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FormWrapper = styled.div<{
	children?: ReactNode;
}>({
	display: 'flex',
	justifyContent: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Blanket = styled.div<{
	hideContent: boolean;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	display: (props) => (props.hideContent ? 'none' : 'inline-flex'),
});
